import {
  SbefScopeGroupKey,
  sbefScopeGroupKeys,
} from '@shared/construction/sbef-custom-groups.data';
import { Lifecycle } from '@shared/models/lifecycles.model';
import { Results, SbefGroupResults } from '@shared/models/unit.model';
import {
  foldConservativeDeltasFlat,
  stripConservativeDeltasFlat,
} from './conservative-carbon.helpers';
import { filterResultsByLifecycles } from './lifecycles.helpers';
import {
  filterResultsBySbefGroups,
  getFlatResults,
  isSbefGroupFilterActive,
} from './sbef-results.helpers';

/**
 * The user-controlled scope applied to result rows before display.
 */
export type ResultsScope = {
  /** Lifecycle phases to include (e.g. A1-A3, A4). */
  lifecycles: Lifecycle[];
  /** Selected SBEF scope group keys (standard + custom A5); empty or all groups means "no SBEF scope". */
  sbefGroups: readonly SbefScopeGroupKey[];
  /**
   * When true, present the conservative carbon view (DEV-3411): the stored conservative deltas
   * ({@link CONSERVATIVE_A1_A3_UNIT}, {@link CONSERVATIVE_A5_UNIT}) are folded on top of their
   * base phases (`co2e_A1-A3`, `co2e_A5`) so every downstream sum reflects the uplift without
   * touching call sites. When false the deltas are stripped so they never count.
   */
  conservative?: boolean;
};

/**
 * Primitive identity for a {@link ResultsScope} so caches can depend on selection
 * without using array or object identity. Omitting `scope` yields an empty token.
 */
export const getResultsScopeCacheKey = (scope?: ResultsScope): string => {
  if (!scope) return '';
  return [
    scope.lifecycles.join(','),
    scope.sbefGroups.join(','),
    String(scope.conservative ?? false),
  ].join('|');
};

/**
 * Apply a flat-bucket transform to a full row — the flat total and every stored SBEF group
 * bucket — so it survives both flat reads and SBEF-group scoping.
 */
const mapRowBuckets = (
  results: Results,
  transform: (bucket: Results) => Results,
): Results => {
  const row = results as SbefGroupResults;
  const next: SbefGroupResults = transform(getFlatResults(row));
  for (const key of sbefScopeGroupKeys) {
    const bucket = row[key];
    if (bucket) next[key] = transform(bucket);
  }
  return next;
};

/**
 * Apply lifecycle and conservative scope to cached {@link Project.results} rows in the project
 * list. SBEF group scoping is omitted because list payloads are flat rollups that often lack a
 * per-group breakdown; filtering by SBEF subset would zero out totals incorrectly.
 */
export const filterProjectListResultsByScope = (
  results: Results,
  {
    lifecycles,
    conservative = false,
  }: Pick<ResultsScope, 'lifecycles' | 'conservative'>,
): Results =>
  filterResultsByScope(results, {
    lifecycles,
    sbefGroups: [],
    conservative,
  });

/**
 * Apply the full result scope to a row: fold in (or strip) the conservative carbon deltas, then
 * collapse to the selected SBEF group buckets (this also strips the stored breakdown), then omit
 * the unselected lifecycle phases.
 * @param results - A stored {@link Results} row (may carry an SBEF group breakdown).
 * @param scope - The selected lifecycles, SBEF groups, and conservative toggle.
 */
export const filterResultsByScope = (
  results: Results,
  { lifecycles, sbefGroups, conservative = false }: ResultsScope,
): Results => {
  const transform = conservative
    ? foldConservativeDeltasFlat
    : stripConservativeDeltasFlat;
  // The stored SBEF group buckets only matter when an SBEF filter is active; otherwise
  // filterResultsBySbefGroups collapses to the flat total, so transforming each bucket is
  // wasted work. Transform just the flat bucket in the common (no SBEF scope) path (DEV-3411).
  const mapped = isSbefGroupFilterActive(sbefGroups)
    ? mapRowBuckets(results, transform)
    : transform(getFlatResults(results));
  return filterResultsByLifecycles(
    filterResultsBySbefGroups(mapped, sbefGroups),
    lifecycles,
  );
};
